import { lazy, Suspense, useMemo } from 'react';
import {
  Box,
  CircularProgress,
  Container,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Navigate, useParams } from 'react-router-dom';

import { DealRoomPills } from '@/components/DealRoom/DealRoomPills';
import { DealRoomSidebar } from '@/components/DealRoom/DealRoomSidebar';
import { DealRoomSidebarTop } from '@/components/DealRoom/DealRoomSidebarTop';
import { NextStepCTA } from '@/components/DealRoom/NextStepCTA';
import {
  isStageLocked,
  SELLER_STAGE_LABELS,
  STAGE_LABELS,
} from '@/components/Property/propertyCardConstants';
import { PropertyEnrichmentProgress } from '@/components/Property/PropertyEnrichmentProgress';
import { useProperty } from '@/hooks/useProperty';
import { useStageNotifications } from '@/hooks/useStageNotifications';
import { SELLER_MAX_STAGE } from '@/utils/propertyView/sellerStageResolver';
import type { PropertyStage, PropertyView } from '@/types/PropertyView';

const Stage1Saved = lazy(
  () => import('@/components/DealRoom/stages/Stage1Saved')
);
const Stage2Qualified = lazy(
  () => import('@/components/DealRoom/stages/Stage2Qualified')
);
const Stage3Applied = lazy(
  () => import('@/components/DealRoom/stages/Stage3Applied')
);
const Stage4Toured = lazy(
  () => import('@/components/DealRoom/stages/Stage4Toured')
);
const Stage5OfferEstimate = lazy(
  () => import('@/components/DealRoom/stages/Stage5OfferEstimate')
);
const Stage6OfferSubmission = lazy(
  () => import('@/components/DealRoom/stages/Stage6OfferSubmission')
);
const Stage7Negotiating = lazy(
  () => import('@/components/DealRoom/stages/Stage7Negotiating')
);
const Stage8Processing = lazy(
  () => import('@/components/DealRoom/stages/Stage8Processing')
);
const Stage9Underwriting = lazy(
  () => import('@/components/DealRoom/stages/Stage9Underwriting')
);
// Stage10Closing is a named export (not default), so map it to the
// `{ default }` shape React.lazy expects. Without this the panel resolves to
// undefined and stage 10 renders blank.
const Stage10Closing = lazy(() =>
  import('@/components/DealRoom/stages/Stage10Closing').then((m) => ({
    default: m.Stage10Closing,
  }))
);

const SellerStageInformationValidation = lazy(
  () =>
    import(
      '@/components/DealRoom/stages/seller/SellerStageInformationValidation'
    )
);
const SellerStageNegotiation = lazy(
  () => import('@/components/DealRoom/stages/seller/SellerStageNegotiation')
);
const SellerStageRatification = lazy(
  () => import('@/components/DealRoom/stages/seller/SellerStageRatification')
);
const SellerStageInspections = lazy(
  () => import('@/components/DealRoom/stages/seller/SellerStageInspections')
);
const SellerStageEscrow = lazy(
  () => import('@/components/DealRoom/stages/seller/SellerStageEscrow')
);
const SellerStageAddendums = lazy(
  () => import('@/components/DealRoom/stages/seller/SellerStageAddendums')
);
const SellerStageClosing = lazy(
  () => import('@/components/DealRoom/stages/seller/SellerStageClosing')
);

type StageComponent = (props: { property: PropertyView }) => JSX.Element;

const wrap =
  (
    Comp: React.LazyExoticComponent<
      (props: { property: PropertyView }) => JSX.Element
    >
  ): StageComponent =>
  (props) =>
    (
      <Suspense fallback={<CircularProgress />}>
        <Comp {...props} />
      </Suspense>
    );

function panelForBuyer(stage: PropertyStage): StageComponent {
  switch (stage) {
    case 1:
      return wrap(Stage1Saved);
    case 2:
      return wrap(Stage2Qualified);
    case 3:
      return wrap(Stage3Applied);
    case 4:
      return wrap(Stage4Toured);
    case 5:
      return wrap(Stage5OfferEstimate);
    case 6:
      return wrap(Stage6OfferSubmission);
    case 7:
      return wrap(Stage7Negotiating);
    case 8:
      return wrap(Stage8Processing);
    case 9:
      return wrap(Stage9Underwriting);
    case 10:
      return wrap(Stage10Closing);
    default:
      return () => <></>;
  }
}

function panelForSeller(stage: PropertyStage): StageComponent {
  switch (stage) {
    case 1:
      return wrap(SellerStageInformationValidation);
    case 2:
      return wrap(SellerStageNegotiation);
    case 3:
      return wrap(SellerStageRatification);
    case 4:
      return wrap(SellerStageInspections);
    case 5:
      return wrap(SellerStageEscrow);
    case 6:
      return wrap(SellerStageAddendums);
    case 7:
      return wrap(SellerStageClosing);
    default:
      return () => <></>;
  }
}

// Shown when a stage ahead of the property's real stage is opened by URL. The
// sidebar disables these stages; the page must agree instead of rendering the
// stage's panel against data that doesn't exist yet.
const LockedStagePanel = ({
  label,
  currentLabel,
}: {
  label: string;
  currentLabel: string;
}) => (
  <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
    <Typography variant="h6" gutterBottom>
      {label} isn't open yet
    </Typography>
    <Typography color="text.secondary">
      This deal is at {currentLabel}. {label} unlocks once the earlier steps are
      done.
    </Typography>
  </Paper>
);

export const DealRoomPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { propertyId, stageNum } = useParams<{
    propertyId: string;
    stageNum?: string;
  }>();
  const { property, product, isEnriching, isLoading } = useProperty(propertyId);
  useStageNotifications(property, isLoading);

  const isSeller = property?.pipelineMode === 'seller';
  const maxStage = isSeller ? SELLER_MAX_STAGE : 10;

  const stageNumber = useMemo<PropertyStage | undefined>(() => {
    if (!stageNum) return undefined;
    const n = Number(stageNum);
    if (!Number.isFinite(n) || n < 1 || n > maxStage) return undefined;
    return n as PropertyStage;
  }, [stageNum, maxStage]);

  if (isLoading) {
    return (
      <Container sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!property) {
    return <Navigate to="/properties" replace />;
  }

  if (!stageNumber) {
    return (
      <Navigate
        to={`/properties/${property.id}/stage/${property.stage}`}
        replace
      />
    );
  }

  if (!isSeller && property.isRefinance && [4, 5, 6, 7].includes(stageNumber)) {
    return (
      <Navigate
        to={`/properties/${property.id}/stage/${property.stage}`}
        replace
      />
    );
  }

  const labels = isSeller ? SELLER_STAGE_LABELS : STAGE_LABELS;
  const Panel: StageComponent = isStageLocked(stageNumber, property)
    ? () => (
        <LockedStagePanel
          label={labels[stageNumber] || `Step ${stageNumber}`}
          currentLabel={labels[property.stage] || `Step ${property.stage}`}
        />
      )
    : isSeller
    ? panelForSeller(stageNumber)
    : panelForBuyer(stageNumber);

  return (
    <Box
      className="taste-page-chrome"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        {!isMobile && (
          <DealRoomSidebar property={property} currentStage={stageNumber} />
        )}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {isMobile && (
            <>
              <DealRoomSidebarTop property={property} />
              <DealRoomPills property={property} currentStage={stageNumber} />
            </>
          )}
          <Container
            sx={{
              py: 3,
              pb: isMobile ? 14 : 12,
              maxWidth: isSeller ? 'lg' : undefined,
            }}
          >
            {isEnriching && !isSeller ? (
              <Box sx={{ mb: 3 }}>
                <PropertyEnrichmentProgress product={product} />
              </Box>
            ) : null}
            <Panel property={property} />
          </Container>
          <NextStepCTA property={property} currentStage={stageNumber} />
        </Box>
      </Box>
    </Box>
  );
};

export default DealRoomPage;
